import { type FC, type PropsWithChildren, useCallback } from 'react';

import type { IconTypes } from '@vakantiesnl/components/src/atoms/Icon';
import { NumberInput } from '@vakantiesnl/components/src/molecules/NumberInput/NumberInput';

import { useStyles } from './MultiSelect.styles';
import { SelectBar, type SelectBarProps } from '../SelectBar';
import { SelectBarContent } from '../SelectBarContent/SelectBarContent';

export type MultiSelectProps = Omit<SelectBarProps, 'content' | 'selector'> & {
	/** Title for the select component */
	title: string;
	icon?: IconTypes;
	maxOptions: number;
	defaultValue?: number;
	/** Callback function with the value */
	onSelect: (selectedValue: number, id: string) => void;
	/** Label to create groups for radios */
	name?: string;
};

export const MultiSelect: FC<PropsWithChildren<MultiSelectProps>> = ({
	id,
	onSelect,
	title,
	children,
	tag,
	defaultValue,
	className,
	dataCy,
	isDisabled,
	maxOptions,
	icon,
}) => {
	const { classes, cx } = useStyles();

	const handleOnUpdate = useCallback(
		(
			_e: React.FocusEvent<HTMLInputElement> | React.PointerEvent | React.KeyboardEvent,
			selectedValue: number | null,
		): void => {
			onSelect(selectedValue ?? 0, id);
		},
		[id, onSelect],
	);

	return (
		<SelectBar
			id={id}
			className={cx(className, classes.selectBarMulti)}
			dataCy={dataCy}
			isDisabled={isDisabled}
			selector={
				<NumberInput
					data-cy={`${dataCy}-select`}
					defaultValue={defaultValue}
					min={0}
					max={maxOptions}
					onChange={handleOnUpdate}
				/>
			}
			content={
				<SelectBarContent icon={icon} title={title} dataCy={dataCy}>
					{children}
				</SelectBarContent>
			}
			tag={tag}
		/>
	);
};
